import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import { FormElement, FormListItem } from './CommonFormComponents';
import { AgencyUserCandidatesDropdown } from './dropdowns/single/AgencyUserCandidates';
import createRelationsText from './AgencyRegistrationApplicationRelationText';
import dialogLoader from '../utilities/dialogLoader';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import { getAgencyRegistrationApplicationStatus } from '../utilities/constantsUtil';
import { TrashIcon } from './icons/TrashIcon';
import AgencyRegistrationApplicationHistoryText from './AgencyRegistrationApplicationHistoryText';
import AgencyRegistrationApplicationAgreement from './AgencyRegistrationApplicationAgreement';
import { PartnerCompanyAddDialog } from '../components/PartnerCompanyAddDialog';
import {
  ONE_MASTER_FLAG,
  PARTNER_COMPANY_REGISTER_LOAD_FROM,
} from '../utilities/constants';
import PrintUtility from '../utilities/printUtility';
import { DownloadFileErrorDialog } from './modals/DownloadFileErrorDialog';
import { getLabelPersonalCompanyType } from '../utilities/partnerCompanyUtil';
import { messageUtility } from '../utilities/messageUtility';

const AgencyRegistrationApplicationForm = ({
  isEdit,
  formValues,
  errorMessages,
  agencyUserCandidates,
  isDifferent,
  onSelectUserCandidate,
  onAddUserCandidate,
  onDeleteUserCandidate,
  onSelectCompany,
  onSetFile,
  onDeleteFile,
  moveDetailPage,
  movePartnerCompanyRegister,
  canEditCompanyAgentTo,
  canEditMemorandumFile,
  setSelectedPartnerCompanyData,
  isPartnerCompanyAddDialogOpen,
  setIsPartnerCompanyAddDialogOpen,
  partnerCompanyAddDialogSearchParam,
  setPartnerCompanyAddDialogSearchParam,
}) => {
  const renderAgentInfo = () => {
    return (
      <div>
        代行先会社の会社情報や作業員情報を代わりに登録する協力会社管理者ユーザーを設定してください。
        <br />
        代行担当者は、申請承認後に代行先一覧から追加することもできます。
      </div>
    );
  };
  const renderOriginalFilenameInfo = () => {
    return (
      <div className='AgencyRegistrationApplicationForm__original-filename-info'>
        最新のテンプレートで覚書を締結しているかご確認ください。
        <div className='AgencyRegistrationApplicationForm__original-filename-info-attention'>
          ※フッターにOBG-001の記載がないテンプレートは、2022年07月01日以降はご利用いただけません。
        </div>
      </div>
    );
  };
  return (
    <ul className='form-list form-list-default AgencyRegistrationApplicationForm set-width-dropdown'>
      {isEdit && (
        <React.Fragment>
          <StatusFormItem status_no={formValues.status_no} />
          <FormListItem label={'申請No.'}>
            <FormElement>{formValues.id}</FormElement>
          </FormListItem>
        </React.Fragment>
      )}
      <SelectCompanyFormItem
        name={formValues.company_agent_to_name}
        companyNumber={formValues.company_agent_to_company_number}
        postal={formValues.company_agent_to_postal_code}
        address={formValues.company_agent_to_address}
        tel={formValues.company_agent_to_tel}
        oneOwnerFlag={formValues.company_agent_to_one_owner_flag}
        oneMasterFlag={formValues.company_agent_to_one_master_flag}
        otherAgenciesCount={formValues.other_agencies_count}
        isDifferent={isDifferent}
        onSelectCompany={onSelectCompany}
        movePartnerCompanyRegister={movePartnerCompanyRegister}
        errorMessage={errorMessages.company_agent_to}
        enable={canEditCompanyAgentTo}
        setSelectedPartnerCompanyData={setSelectedPartnerCompanyData}
        setIsPartnerCompanyAddDialogOpen={setIsPartnerCompanyAddDialogOpen}
        isPartnerCompanyAddDialogOpen={isPartnerCompanyAddDialogOpen}
        partnerCompanyAddDialogSearchParam={partnerCompanyAddDialogSearchParam}
        setPartnerCompanyAddDialogSearchParam={
          setPartnerCompanyAddDialogSearchParam
        }
        kokuzei_name={formValues.kokuzei_name}
        kokuzei_company_number={formValues.kokuzei_company_number}
        kokuzei_address={formValues.kokuzei_address}
      />
      <FormListItem label={'代行担当者'} required={true}>
        <FormElement
          validate={errorMessages.company_agent_to_users}
          info={renderAgentInfo()}
        >
          <div className='form-container'>
            {_.map(agencyUserCandidates, (user, index) => (
              <AgencyUserCandidatesDropdown
                index={index}
                userCandidatesSelected={agencyUserCandidates}
                onSelectUserCandidate={onSelectUserCandidate}
                onDeleteUserCandidate={onDeleteUserCandidate}
                key={index}
              />
            ))}
            <div className='form-container__item'>
              <button className='btn--sub' onClick={onAddUserCandidate}>
                追加する
              </button>
            </div>
          </div>
        </FormElement>
      </FormListItem>
      <FormListItem label={'覚書の添付'} required={canEditMemorandumFile}>
        <FormElement
          validate={
            errorMessages.original_filename || errorMessages.memorandum_file
          }
          info={renderOriginalFilenameInfo()}
        >
          <FileUploadButton
            fileName={formValues.original_filename}
            onSetFile={onSetFile}
            onDeleteFile={onDeleteFile}
            enable={canEditMemorandumFile}
          />
        </FormElement>
      </FormListItem>
      {isEdit && (
        <React.Fragment>
          <FormListItem label='履歴'>
            <FormElement>
              <div className='form-container'>
                <AgencyRegistrationApplicationHistoryText
                  title={'登録日時'}
                  date={formValues.registered_at}
                />
                <AgencyRegistrationApplicationHistoryText
                  title={'申請日時'}
                  date={formValues.applied_at}
                />
                <AgencyRegistrationApplicationHistoryText
                  title={'承認日時'}
                  date={formValues.approved_at}
                />
                <AgencyRegistrationApplicationHistoryText
                  title={'完了日時'}
                  date={formValues.released_at}
                />
              </div>
            </FormElement>
          </FormListItem>
          <FormListItem label='関連する申請'>
            <FormElement>
              <div className='form-container'>
                {createRelationsText(
                  formValues.id,
                  formValues.related_applications,
                  moveDetailPage
                )}
              </div>
            </FormElement>
          </FormListItem>
        </React.Fragment>
      )}
      <AgencyRegistrationApplicationAgreement />
    </ul>
  );
};

const StatusFormItem = ({ status_no }) => {
  return (
    <React.Fragment>
      <FormListItem label='ステータス' className={'form-list__item'}>
        <FormElement>
          {getAgencyRegistrationApplicationStatus(status_no)}
        </FormElement>
      </FormListItem>
    </React.Fragment>
  );
};

const SelectCompanyFormItem = ({
  name,
  companyNumber,
  postal,
  address,
  tel,
  oneOwnerFlag,
  oneMasterFlag,
  otherAgenciesCount,
  isDifferent,
  onSelectCompany,
  movePartnerCompanyRegister,
  errorMessage,
  enable,
  setSelectedPartnerCompanyData,
  setIsPartnerCompanyAddDialogOpen,
  isPartnerCompanyAddDialogOpen,
  partnerCompanyAddDialogSearchParam,
  setPartnerCompanyAddDialogSearchParam,
  kokuzei_name,
  kokuzei_company_number,
  kokuzei_address,
}) => {
  const [visibleDownloadErrorDialog, setVisibleDownloadErrorDialog] = useState(
    false
  );
  const isRequestingFlag = useRef(false);
  const showPartnerCompanyAddDialog = () => {
    const options = {};
    if (!_.isEmpty(partnerCompanyAddDialogSearchParam)) {
      options.cacheParam = partnerCompanyAddDialogSearchParam;
    }

    dialogLoader.show(PartnerCompanyAddDialog, {
      state: {
        similarCompanyExists: false,
        options,
      },
      loadFrom: PARTNER_COMPANY_REGISTER_LOAD_FROM.AGENCY_REGISTER,
      callback: {
        close: () => {
          // 登録をやめる
          dialogLoader.close();
          setPartnerCompanyAddDialogSearchParam({});
          setIsPartnerCompanyAddDialogOpen(false);
        },
        create: () => {
          setSelectedPartnerCompanyData(undefined);
          movePartnerCompanyRegister();
          dialogLoader.close();
          setIsPartnerCompanyAddDialogOpen(false);
        },
        submit: async params => {
          onSelectCompany(params);
          dialogLoader.close();
          setIsPartnerCompanyAddDialogOpen(false);
        },
        // リストからBuildee未加入法人を選択するとき
        createCompany: data => {
          setSelectedPartnerCompanyData(data);
          movePartnerCompanyRegister();
          dialogLoader.close();
          setIsPartnerCompanyAddDialogOpen(false);
        },
      },
    });
  };

  const downloadFileManual = useCallback(async () => {
    if (isRequestingFlag.current) {
      return;
    }
    isRequestingFlag.current = true;
    await PrintUtility.downloadStaticFilePDF(
      'agencyRegistrationManual',
      '代行登録説明資料（機能概要・覚書記入方法）.pdf',
      setVisibleDownloadErrorDialog
    );
    isRequestingFlag.current = false;
  }, []);

  const isSelected = !isNullOrUndefinedOrEmptyStr(name);

  const isPersonalCompany = oneMasterFlag === ONE_MASTER_FLAG.personal.id;

  useEffect(
    () => {
      if (isPartnerCompanyAddDialogOpen) {
        showPartnerCompanyAddDialog();
      }
    },
    [isPartnerCompanyAddDialogOpen]
  );

  return (
    <React.Fragment>
      <FormListItem
        label={'代行先会社'}
        className={
          isSelected
            ? 'form-list__item form-list__item--open'
            : 'form-list__item'
        }
        required={enable}
      >
        <FormElement validate={errorMessage}>
          <div>
            {enable && (
              <button
                className={`btn--sub`}
                onClick={() => {
                  setIsPartnerCompanyAddDialogOpen(true);
                }}
              >
                会社を選択
              </button>
            )}
          </div>
        </FormElement>
      </FormListItem>
      {isSelected && (
        <React.Fragment>
          <FormListItem
            label={' '}
            className='form-list__item form-list__item--child'
            style={{ backgroundColor: 'initial', fontWeight: 'bold' }}
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              <span>Buildee登録情報</span>
            </FormElement>
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              <span>国税庁法人登録情報</span>
            </FormElement>
          </FormListItem>
          <FormListItem
            label={'会社名'}
            className='form-list__item form-list__item--child'
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {name}
            </FormElement>
            <FormElement
              className={
                isDifferent.includes('name')
                  ? 'form-list__element AgencyRegistrationApplicationForm__company-info-element AgencyRegistrationApplicationForm__company-info-element--different'
                  : 'form-list__element AgencyRegistrationApplicationForm__company-info-element'
              }
            >
              {isPersonalCompany
                ? '個人事業主のため国税庁法人登録情報はありません。'
                : isNullOrUndefinedOrEmptyStr(kokuzei_company_number)
                ? '登録情報なし'
                : kokuzei_name}
            </FormElement>
          </FormListItem>
          <FormListItem
            label={'法人種別'}
            className='form-list__item form-list__item--child'
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {isPersonalCompany
                ? ONE_MASTER_FLAG.personal.name
                : `${
                    ONE_MASTER_FLAG.corporation.name
                  }（法人番号：${companyNumber}）`}
            </FormElement>
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {isNullOrUndefinedOrEmptyStr(kokuzei_company_number)
                ? ''
                : `${
                    ONE_MASTER_FLAG.corporation.name
                  }（法人番号：${kokuzei_company_number}）`}
            </FormElement>
          </FormListItem>
          {isPersonalCompany && (
            <FormListItem
              label={messageUtility.getMessage('I-000-186')}
              className='form-list__item form-list__item--child'
            >
              <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
                {getLabelPersonalCompanyType(oneOwnerFlag)}
              </FormElement>
            </FormListItem>
          )}
          <FormListItem
            label={'住所'}
            className='form-list__item form-list__item--child'
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {address}
            </FormElement>
            <FormElement
              className={
                isDifferent.includes('address')
                  ? 'form-list__element AgencyRegistrationApplicationForm__company-info-element AgencyRegistrationApplicationForm__company-info-element--different'
                  : 'form-list__element AgencyRegistrationApplicationForm__company-info-element'
              }
            >
              {kokuzei_address}
            </FormElement>
          </FormListItem>
          <FormListItem
            label={'電話番号'}
            className='form-list__item form-list__item--child'
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {tel}
            </FormElement>
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {!isPersonalCompany &&
                !isNullOrUndefinedOrEmptyStr(kokuzei_company_number) &&
                '-'}
            </FormElement>
          </FormListItem>
          <FormListItem
            label={'代行実施中の会社数（自社を除く）'}
            className='form-list__item form-list__item--child'
          >
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {_.isNil(otherAgenciesCount) ? '' : otherAgenciesCount}
            </FormElement>
            <FormElement className='form-list__element AgencyRegistrationApplicationForm__company-info-element'>
              {!isPersonalCompany &&
                !isNullOrUndefinedOrEmptyStr(kokuzei_company_number) &&
                '-'}
            </FormElement>
          </FormListItem>
          {!_.isEmpty(isDifferent) && (
            <FormListItem
              label={' '}
              className='form-list__item form-list__item--child'
              style={{ backgroundColor: 'initial', padding: 0 }}
            >
              <FormElement>
                <span
                  className='AgencyRegistrationApplicationForm__original-filename-info-attention'
                  style={{ fontSize: 12, paddingLeft: 4, paddingTop: 12 }}
                >
                  Buildeeと国税庁の登録情報に差異があります。代行登録説明資料（機能概要・覚書記入方法）の「申請前確認事項」をご確認ください。
                  <br />
                  ひとつ前のページでダウンロードしていない場合は
                  <span
                    className='clickable-text'
                    onClick={downloadFileManual}
                  >
                    こちら
                  </span>
                  からダウンロードしてください。
                </span>
              </FormElement>
            </FormListItem>
          )}
          <DownloadFileErrorDialog
            visible={visibleDownloadErrorDialog}
            onClose={() => setVisibleDownloadErrorDialog(false)}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const FileUploadButton = ({ fileName, onSetFile, onDeleteFile, enable }) => {
  return (
    <div className='form-container'>
      {fileName && (
        <React.Fragment>
          <span>{fileName || ''}</span>
          {enable && (
            <button
              className='btn-icon--xs sys-delete form-list__element--btn_delete'
              onClick={onDeleteFile}
            >
              <TrashIcon className='icon--2_4x form-list__trash-icon' />
            </button>
          )}
        </React.Fragment>
      )}
      {enable && (
        <div className={fileName && 'form-container__item'}>
          <label htmlFor='file' className='btn input-file'>
            ファイル選択
            <input
              id='file'
              name='memorandum_file'
              type='file'
              accept='application/pdf'
              onChange={e => onSetFile(e.target.files[0])}
              onClick={e => {
                e.target.value = '';
              }}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default AgencyRegistrationApplicationForm;



// WEBPACK FOOTER //
// ./src/components/AgencyRegistrationApplicationForm.jsx